import { cloneDeep, set } from "lodash";
import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import ApiStateModal from "../../../components/reusable-ui/basic-modal";
import { MESSAGE_TYPE, COMPONENT_MODES } from "../../../data/constants";
import { isAddNewMode, isEditMode } from "../../../helpers/catalog-helper";
import { getAllMenusData, saveMenuData } from "../../../redux/slices";
import { RootState } from "../../../redux/store";
import MenusDetailsForm from "./menu-details";
import MenusViewComponent from "./menus-view";
import "./style.scss";

const MenusManagement: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const [menuId, setMenuId] = useState<string>("");
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [errorMessageMap, setErrorMsgMap] = useState<any>({});
  const [menuDetails, setMenuDetails] = useState<any>({});
  const [modeConfig, setModeConfig] = useState<any>({
    //it will determine whether it's in view mode or edit mode
    type: COMPONENT_MODES.viewMode,
    tabId: "",
  });
  const menusDataMap = useSelector(
    (state: RootState) => state.menusReducer.menusData
  );
  const [menuNamesList, setMenuNamesList] = useState<any>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalText, setSuccessModalText] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllMenusData());
  }, []);

  useEffect(() => {
    if (menusDataMap && Object.keys(menusDataMap)?.length) {
      let dataToUpdate: any = menusDataMap
        ? Object.values(menusDataMap).map((menuData: any, index: number) => {
            if (menuId !== menuData?.mid) {
              return menuData?.name;
            }
          })
        : [];
      setMenuNamesList(dataToUpdate);
    }
  }, [menusDataMap, menuId]);

  useEffect(() => {
    if (isAddNewMode(modeConfig.type) || !menuId) {
      setMenuDetails({});
      setErrorMsgMap({});
    }
  }, [modeConfig, menuId]);

  useEffect(() => {
    if (!isAddNewMode(modeConfig?.type) && menuId) {
      setMenuDetails(menusDataMap[menuId]);
    }
  }, [menuId, menusDataMap]);

  const onDiscardCtaClick = () => {
    resetStateOnClose();
    if (!isAddNewMode(modeConfig?.type)) {
      if (menuId) {
        setMenuDetails(cloneDeep(menusDataMap[menuId]));
      } else {
        setMenuDetails({});
      }
    }
  };

  const validateMenuDetails = (menuDetails: any) => {
    if (menuDetails?.name && menuNamesList.indexOf(menuDetails?.name) === -1) {
      return { isValid: true, dataToPost: menuDetails };
    }
    let errorMsgMap: any = {};

    if (!menuDetails?.name) {
      errorMsgMap.name = "This is mandatory field";
    }

    if (menuDetails?.name && menuNamesList.indexOf(menuDetails?.name) !== -1) {
      errorMsgMap.name = "Name should be unique";
    }

    return { isValid: false, errorMsgMap };
  };

  const onSaveCtaClick = () => {
    const validatedData: any = validateMenuDetails(menuDetails);
    const callback = (response: any) => {
      if (isEditMode(modeConfig?.type)) {
        // dispatch(updateChargesList({ key: menuId, data: response }));
        setSuccessModalText("Changes Saved Successfully");
      } else if (isAddNewMode(modeConfig?.type)) {
        // dispatch(getAllMenusData());
        setSuccessModalText("Menu Created Successfully");
      }
      dispatch(getAllMenusData());

      setShowSuccessModal(true);
      resetStateOnClose();
    };
    if (validatedData?.isValid) {
      dispatch(
        saveMenuData({
          dataToPost: menuDetails,
          callback,
        })
      );
    } else {
      setErrorMsgMap(validatedData?.errorMsgMap);
    }
  };

  const updateCurDataInParentState = (key: string, value: any) => {
    const dataToUpdate = cloneDeep(menuDetails) || {};
    set(dataToUpdate, key, value);
    setMenuDetails(dataToUpdate);
    setIsChangesMade(true);
  };

  function resetStateOnClose() {
    setErrorMsgMap({});
    setIsChangesMade(false);

    setModeConfig({ type: COMPONENT_MODES.viewMode });
  }

  function clearCurData() {
    setMenuDetails({});
  }

  const commonProps = {
    curData: menuDetails,
    modeConfig,
    setModeConfig,
    updateCurDataInParentState,
    curDataId: menuId,
    errorMessageMap,
    onSaveCtaClick,
    onDiscardCtaClick,
    isChangesMade,
    clearCurData,
  };

  return (
    <section className="flex h-[calc(100vh-141px)] overflow-hidden sm:flex-col menusManageContainer">
      <MenusViewComponent openDetailsView={setMenuId} {...commonProps} />

      {menuId ? <MenusDetailsForm {...commonProps} /> : null}

      {showSuccessModal && (
        <ApiStateModal
          modalType={MESSAGE_TYPE.success}
          open={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
          mainText={successModalText}
        ></ApiStateModal>
      )}
    </section>
  );
};

export default MenusManagement;
